import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Home, RotateCcw, Landmark } from 'lucide-react';
import confetti from 'canvas-confetti';
import axios from 'axios';

const STAR_URL =
  'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/51c2f4d391b08733a1282c5c803c88045bd7e3e5993310b66fbe2ae387825c7c.png';
const MON_BG = 'https://images.unsplash.com/photo-1564507592333-c60657eea523?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxUYWolMjBNYWhhbCUyMEFncmF8ZW58MHx8fHwxNzkxNTYyMjg4fDA&ixlib=rb-4.1.0&q=85';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MonumentResultsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const savedRef = useRef(false);

  const score = parseInt(searchParams.get('score') || '0', 10);
  const total = parseInt(searchParams.get('total') || '15', 10);
  const percentage = total > 0 ? Math.round((score / total) * 100) : 0;
  const stars = percentage >= 90 ? 3 : percentage >= 70 ? 2 : percentage >= 50 ? 1 : 0;

  useEffect(() => {
    if (!savedRef.current) {
      savedRef.current = true;
      axios
        .post(`${API}/monument/results`, {
          player_name: localStorage.getItem('gaurik_player_name') || 'Player',
          score,
          total_questions: total,
        })
        .catch(console.error);
    }
    if (percentage >= 50) {
      const end = Date.now() + 2500;
      const frame = () => {
        confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors: ['#a8a29e', '#d97706', '#0d9488'] });
        confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors: ['#a8a29e', '#d97706', '#0d9488'] });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
    }
  }, [percentage, score, total]);

  const getMessage = () => {
    if (percentage >= 90) return 'MONUMENT MASTER!';
    if (percentage >= 70) return 'GREAT EXPLORER!';
    if (percentage >= 50) return 'NICE TRIP!';
    return 'KEEP EXPLORING!';
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-6"
      style={{ backgroundImage: `url(${MON_BG})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}
    >
      <div className="bg-white/95 backdrop-blur-md border-4 border-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
        <Landmark className="w-12 h-12 mx-auto mb-3 text-stone-600" />

        <h1 data-testid="monument-results-message" className="font-heading text-3xl sm:text-4xl font-black text-gray-900 mb-1">
          {getMessage()}
        </h1>
        <p className="font-body text-sm text-gray-500 mb-5">
          {percentage >= 70 ? "You know India's famous places!" : 'Every photo teaches you something new!'}
        </p>

        <div className="flex justify-center gap-3 mb-5" data-testid="monument-results-stars">
          {[1, 2, 3].map((i) => (
            <img
              key={i}
              src={STAR_URL}
              alt={`star ${i}`}
              className={`w-14 h-14 animate-star-in ${i <= stars ? '' : 'grayscale opacity-30'}`}
              style={{ animationDelay: `${i * 250}ms` }}
            />
          ))}
        </div>

        <p data-testid="monument-results-score" className="font-heading text-5xl font-black text-gray-900 mb-1">
          {score}/{total}
        </p>
        <p className="font-body text-base font-bold text-gray-500 mb-6">{percentage}% correct</p>

        <div className="flex flex-col gap-3">
          <button
            data-testid="monument-play-again"
            onClick={() => navigate('/monument/play')}
            className="bg-gradient-to-r from-stone-600 to-stone-800 text-white font-black text-lg py-4 px-8 rounded-full border-b-[6px] border-stone-950 active:border-b-0 active:translate-y-[6px] transition-all hover:from-stone-500 hover:to-stone-700 flex items-center justify-center gap-2 shadow-lg"
          >
            <RotateCcw className="w-6 h-6" />
            Play Again
          </button>
          <button
            data-testid="monument-go-home"
            onClick={() => navigate('/')}
            className="bg-gray-800 text-white font-black text-lg py-4 px-8 rounded-full border-b-[6px] border-gray-950 active:border-b-0 active:translate-y-[6px] transition-all hover:bg-gray-700 flex items-center justify-center gap-2 shadow-lg"
          >
            <Home className="w-6 h-6" />
            Back to Studio
          </button>
        </div>
      </div>
    </div>
  );
}
